import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { SitemapGenerationResult } from '@/types/admin';

export default function SitemapGeneratorTrigger() {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SitemapGenerationResult | null>(null);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const triggerSitemapGeneration = async () => {
    setIsLoading(true);
    setStatus('idle');
    try {
      // Call the edge function that regenerates sitemaps
      const { data, error } = await supabase.functions.invoke('sitemap-generator-cron', {
        body: { timestamp: new Date().toISOString() }
      });
      
      if (error) {
        console.error('Error triggering sitemap generation:', error);
        toast.error(`Failed to trigger sitemap generation: ${error.message}`);
        setResults({ 
          success: false, 
          message: 'Failed to trigger sitemap generation',
          error: error.message 
        });
        setStatus('error');
        return;
      }
      
      // console.log('Sitemap generation triggered successfully:', data);
      toast.success('Sitemap generation completed successfully.');
      
      setResults(data);
      setStatus('success');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Exception triggering sitemap generation:', error);
      toast.error(`Exception: ${errorMessage}`);
      setResults({ 
        success: false, 
        message: 'Exception occurred during sitemap generation',
        error: errorMessage 
      });
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Sitemap Generator</h2>
      <p className="mb-4 text-gray-600">
        Click the button below to manually trigger sitemap generation. 
        This will create or update all sitemap files and store them for faster access.
      </p>
      
      {status === 'error' && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to generate sitemaps. Please check the logs for more details.
          </AlertDescription>
        </Alert>
      )}
      
      {status === 'success' && (
        <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription>
            Sitemaps generated successfully!
          </AlertDescription>
        </Alert>
      )}
      
      <Button 
        onClick={triggerSitemapGeneration}
        disabled={isLoading}
        className="flex gap-2 items-center"
      >
        {isLoading ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Generating...</span>
          </>
        ) : (
          <>Generate Sitemaps Now</>
        )}
      </Button>
      
      <div className="mt-6">
        <h3 className="text-lg font-medium mb-2">Sitemap URLs</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><a href="/sitemap.xml" target="_blank" className="text-blue-600 hover:underline">Main Sitemap Index</a></li>
          <li><a href="/sitemaps/main.xml" target="_blank" className="text-blue-600 hover:underline">Main Pages Sitemap</a></li>
          <li><a href="/sitemaps/nursing-homes.xml" target="_blank" className="text-blue-600 hover:underline">Nursing Homes Sitemap</a></li>
          <li><a href="/sitemaps/counties.xml" target="_blank" className="text-blue-600 hover:underline">Counties Sitemap</a></li>
          <li><a href="/sitemaps/content.xml" target="_blank" className="text-blue-600 hover:underline">Content Sitemap</a></li>
        </ul>
      </div>
      
      {results && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Results</h3>
          <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-60">
            {JSON.stringify(results, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
